import { LocationStrategy, PathLocationStrategy, ViewportScroller } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StorageMap, StorageModule } from '@ngx-pwa/local-storage';
import { TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { HotkeyModule } from 'angular2-hotkeys';
import { DISQUS_SHORTNAME } from 'ngx-disqus';
import { NG_SCROLLBAR_OPTIONS, provideScrollbarOptions } from 'ngx-scrollbar';
import { ToastrModule } from 'ngx-toastr';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FooterModule } from './components/footer/footer.module';
import { MaintenanceModule } from './components/maintenance/maintenance.module';
import { ToastComponent } from './components/toast/toast.component';
import { TranslationKeyToggleComponent } from './components/translation-key-toggle/translation-key-toggle.component';
import { GlobalErrorHandler } from './handlers/global-error.handler';
import { ClientIdentificationInterceptor } from './interceptors/client-identification.interceptor';
import { HttpErrorHandlingInterceptor } from './interceptors/http-error-handling.interceptor';
import { CustomTranslationLoaderService } from './services/custom-translation-loader.service';

import { InitializeService } from './services/initialize.service';
import { LanguageService } from './services/language.service';
import { ChatSharedModule } from './shared/components/chat-shared/chat-shared.module';
import { SharedModule } from './shared/shared.module';
import { ContentVisibilityInterceptor } from './interceptors/content-visibility.interceptor';
import {
  LocalStorageService,
  MaintenanceInterceptor,
  storageKeys,
  BrowserStorage,
  STORAGE_KEYS,
  SharedLibraryModule,
  SharedTranslateCompilerService,
  PreSignedUrlInterceptor,
  CookieCategory,
  RECORD_MEDIA_SERVICE_PROVIDER
} from 'library-explorer';
import { ApplicationVersionInterceptor } from './interceptors/application-version.interceptor';
import { NgxMatomoTrackerModule, MatomoInitializationMode } from '@ngx-matomo/tracker';
import { NgxMatomoRouterModule } from '@ngx-matomo/router';
import { MatomoCustomRouteInterceptor } from './interceptors/matomo-custom-route.interceptor';
import { BranchIdentificationInterceptor } from './interceptors/branch-identification.interceptor';
import { AcceptLanguageInterceptor } from './interceptors/accept-language.interceptor';
import { OfflineRequestInterceptor } from './interceptors/offline-request.interceptor';
import { environment } from '@env/environment';
import { Event, NavigationEnd, Router, Scroll } from '@angular/router';
import { SettingsService } from './services/settings.service';
import { SkipTokenInterceptor } from './interceptors/skip-token.interceptor';
import { LoadingTokenInterceptor } from './interceptors/loading-token.interceptor';
import { LoadingToastComponent } from './components/loading-toast/loading-toast.component';
import { ScrollPositionInterceptor } from './interceptors/scroll-position.interceptor';
import { filter, pairwise } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { NavigateService } from './services/navigate.service';
import { OneTrustModule } from '@altack/ngx-onetrust';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { BROWSER_NAVIGATION_SERVICE_PROVIDER } from './services/browser-navigation/browser-navigation.provider';

export function init_app(initializeService: InitializeService, injector: Injector) {
  return () => initializeService.init(injector);
}

const disableAnimations = !('animate' in document.documentElement)
  || (navigator && /iPhone OS (8|9|10|11|12|13)_/.test(navigator.userAgent));

@NgModule({
  declarations: [
    AppComponent,
    ToastComponent,
    TranslationKeyToggleComponent,
    LoadingToastComponent
  ],
  bootstrap: [AppComponent], 
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule.withConfig({ disableAnimations }),
    SharedModule,
    ChatSharedModule,
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-bottom-center',
      preventDuplicates: true,
      toastComponent: ToastComponent,
    }),
    StorageModule.forRoot({ IDBNoWrap: true }),
    AngularSvgIconModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslationLoaderService,
        deps: [HttpClient, Injector, StorageMap]
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: SharedTranslateCompilerService
      }
    }),
    NgxMatomoTrackerModule.forRoot({
      mode: MatomoInitializationMode.AUTO_DEFERRED,
    }),
    NgxMatomoRouterModule.forRoot({
      interceptors: [MatomoCustomRouteInterceptor],
    }),
    FooterModule,
    HotkeyModule.forRoot(),
    MaintenanceModule,
    SharedLibraryModule.forRoot({
      apiUrl: environment.apiUrl,
      apiCoreAppUrl: environment.apiCoreAppUrl,
      iconOverrideFromSettings: true, 
      settingsClassProvider: SettingsService 
    }),
    OneTrustModule.forRoot({
      cookiesGroups: {
        StrictlyNecessaryCookies: CookieCategory.NECCESSARY,
        PerformanceCookies: CookieCategory.PERFORMANCE,
        FunctionalCookies: CookieCategory.FUNCTIONAL,
        TargetingCookies: CookieCategory.TARGETING,
        SocialMediaCookies: CookieCategory.SOCIAL_MEDIA
      }
    }),
    NgxExtendedPdfViewerModule
  ],
  providers: [
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    InitializeService,
    LanguageService,
    BROWSER_NAVIGATION_SERVICE_PROVIDER,
    {
      provide: STORAGE_KEYS,
      useValue: storageKeys
    },
    {
      provide: BrowserStorage,
      useClass: LocalStorageService
    },
    provideAppInitializer(() => {
      const initializerFn = (init_app)(inject(InitializeService), inject(Injector));
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingTokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OfflineRequestInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorHandlingInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ClientIdentificationInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BranchIdentificationInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ContentVisibilityInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApplicationVersionInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AcceptLanguageInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SkipTokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ScrollPositionInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PreSignedUrlInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    {
      provide: NG_SCROLLBAR_OPTIONS,
      useValue: {
        scrollAuditTime: 20
      }
    },
    {
      provide: DISQUS_SHORTNAME,
      useValue: 'thelearning-lab'
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideScrollbarOptions({
      orientation: 'auto'
    })
  ]
})

export class AppModule {

  constructor(router: Router, injector: Injector) {
    router.events.pipe(
      filter((event: Event) => event instanceof Scroll),
      pairwise()
    ).subscribe(([previousEvent, event]: [Scroll, Scroll]) => {
      const scrollElement = document.getElementById('full-layout-scroll-section');
      const navigateService = injector.get(NavigateService);
      const viewportScroller = injector.get(ViewportScroller);

      // Capture the current URL after redirection
      if (event.routerEvent instanceof NavigationEnd) {
        navigateService.currentPageUrl = event.routerEvent.urlAfterRedirects;
      }

      // Update previous page URL and scroll position
      if (Capacitor.isNativePlatform() && navigateService.prevPageUrl !== navigateService.currentPageUrl) {
        if (previousEvent.routerEvent instanceof NavigationEnd) {
          navigateService.prevPageUrl = previousEvent.routerEvent.urlAfterRedirects;
        }

        navigateService.prevPageScrollPos = scrollElement
          ? [scrollElement.scrollLeft, scrollElement.scrollTop]
          : viewportScroller.getScrollPosition();
      }

      if (event.position) {
        scrollElement
          ? scrollElement.scrollTo({ left: event.position[0], top: event.position[1] })
          : viewportScroller.scrollToPosition(event.position);
        return;
      }

      // Handle anchor navigation
      if (event.anchor) {
        viewportScroller.scrollToAnchor(event.anchor);
        return;
      }

      if (previousEvent.routerEvent instanceof NavigationEnd && event.routerEvent instanceof NavigationEnd) {
        // Reset scroll position if navigating to a new page
        const prevUrl = previousEvent?.routerEvent?.urlAfterRedirects?.split('?')[0];
        const currentUrl = event?.routerEvent?.urlAfterRedirects?.split('?')[0];

        if (prevUrl !== currentUrl) {
          scrollElement
            ? scrollElement.scrollTo({ left: 0, top: 0 })
            : viewportScroller.scrollToPosition([0, 0]);
        }
      }

    });
  }

}
